import { getAuthToken, isTokenExpired, parseToken } from "utils/tokenManager"
import { clearAuthAndTenantStorage } from "utils/sessionCleanup"
import logger from "utils/logger"

/**
 * Check whether the current user is authenticated.
 *
 * Combines the presence of a stored token with a non-expired check so that
 * stale "logged-in" state in localStorage does not grant false access.
 *
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  try {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true"
    if (!isLoggedIn) return false
    const token = getAuthToken()
    if (!token) return false
    return !isTokenExpired(token)
  } catch (err) {
    logger.error("isAuthenticated error", err)
    return false
  }
}

/**
 * Return the current user's account type from localStorage.
 *
 * @returns {string|null}
 */
export const getAccountType = () => {
  try {
    return localStorage.getItem("accountType") || null
  } catch {
    return null
  }
}

export const getAccountScope = () => {
  try {
    return localStorage.getItem("accountScope") || null
  } catch {
    return null
  }
}

/**
 * Return the stored auth user object.
 *
 * @returns {object|null}
 */
export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("authUser")
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

/**
 * Check if the current user is a super-admin.
 *
 * Super-admins bypass all permission checks.
 *
 * @returns {boolean}
 */
export const isSuperAdmin = () =>
  getAccountScope() === "super_admin" || getAccountType() === "superadmin"

/**
 * Decode and return the payload from the stored access token.
 *
 * @returns {object|null}
 */
export const getTokenPayload = () => {
  const token = getAuthToken()
  if (!token) return null
  return parseToken(token)
}

/**
 * Check how many milliseconds remain before the stored token expires.
 *
 * @returns {number} Remaining ms, or 0 if already expired / no token.
 */
export const getTokenTtlMs = () => {
  const payload = getTokenPayload()
  if (!payload?.exp) return 0
  const expiryMs = payload.exp * 1000
  const remaining = expiryMs - Date.now()
  return remaining > 0 ? remaining : 0
}

/**
 * Clear all auth-related keys from localStorage.
 * Prefer endSession() when Redux and tenant branding must also reset.
 */
export const clearAuthStorage = () => {
  try {
    clearAuthAndTenantStorage()
  } catch (err) {
    logger.error("clearAuthStorage error", err)
  }
}
